import React, { useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Link,
  Typography,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import Button from '../mui/button';
import FieldsWrapper from '../form/fields-wrapper';
import ReCAPTCHA from 'react-google-recaptcha';
import { sendMail } from '../../services/mail';
import { Controller, useForm } from 'react-hook-form';

const useFormStyles = makeStyles((theme) => ({
  error: {
    lineHeight: '15px',
    color: theme.palette.error.main,
  },
  wrapper: {
    display: 'inline-block',
    marginTop: theme.spacing(1),
    position: 'relative',
  },
  buttonProgress: {
    color: theme.palette.violet.dark,
    position: 'absolute',
    top: '50%',
    left: '50%',
    marginTop: -12,
    marginLeft: -12,
  },
  recaptcha: {
    marginBottom: 20,
    transform: 'scale(1)',
    transformOrigin: '0 0',
    [theme.breakpoints.down('md')]: {
      transform: 'scale(0.8)',
    },
    [theme.breakpoints.down('sm')]: {
      transform: 'scale(1)',
    },
  },
  field: {
    marginBottom: 12,
    '& [class*="MuiInputBase-formControl"]': {
      marginBottom: '0 !important',
    },
  },
  label: {
    display: 'block',
    color: '#9998A5',
    fontFamily: 'Visby CF, sans-serif',
    fontSize: 15,
    fontWeight: 700,
    lineHeight: 1.2,
    marginBottom: 6,
    textTransform: 'lowercase',
  },
  input: {
    '& [class*="MuiOutlinedInput-root"]': {
      minHeight: 64,
    },
    '& [class*="MuiOutlinedInput-input"]:not([class*="MuiOutlinedInput-inputMultiline"])':
      {
        padding: '22px 26px',
      },
    '& [class*="MuiOutlinedInput-multiline"]': {
      minHeight: 100,
      padding: 0,
    },
    '& [class*="MuiOutlinedInput-inputMultiline"]': {
      padding: '18px 16px',
      minHeight: 100,
      boxSizing: 'border-box',
    },
  },
}));

const ContactForm: React.FC = () => {
  const [recaptchaError, setRecaptchaError] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitFailure, setSubmitFailure] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const FormStyles = useFormStyles();
  const siteKey =
    process.env.NODE_ENV === 'production'
      ? '6LdDdwsUAAAAAEBGoOeXYGojxDmc4Ol4EwVn7kE5'
      : '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
  const recaptchaRef = useRef<any>(null);
  let formContent: any;

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm();

  const onRecaptchaChange = async () => {
    if (!recaptchaRef.current.getValue()) {
      setRecaptchaError(true);
    } else {
      setRecaptchaError(false);
    }
  };

  const onSubmit = async (data: any) => {
    if (!recaptchaRef.current.getValue()) {
      setRecaptchaError(true);
      return;
    }

    setRecaptchaError(false);

    const from = data.email;
    const subject = 'New contact form submission';

    const bodyParts: string[] = [
      `<p><strong>From:</strong> ${data.firstName} ${data.lastName} <${data.email}></p>`,
    ];

    if (data.message.length) {
      bodyParts.push(`<p><strong>Message:</strong> ${data.message}</p>`);
    }

    const message = bodyParts.join('');

    setSubmitting(true);

    sendMail('info@marvelandsnap.com', from, subject, message)
      .then(() => {
        setSubmitSuccess(true);
        setSubmitting(false);
      })
      .catch(() => {
        setSubmitFailure(false);
      });
    await new Promise((r) => setTimeout(r, 500));
  };

  if (submitSuccess) {
    formContent = (
      <Box>
        <Typography
          variant="subtitle1"
          component="p"
          color="textSecondary"
          gutterBottom={true}
        >
          Thank you for getting in touch!
        </Typography>
        <Typography variant="body2" gutterBottom={true}>
          We will look over your message and get back in touch with you very
          shortly.
        </Typography>
        <Typography variant="body2">Have a great day!</Typography>
      </Box>
    );
  } else if (submitFailure) {
    formContent = (
      <Box>
        <Typography
          variant="subtitle1"
          component="p"
          color="textSecondary"
          gutterBottom={true}
        >
          Something went wrong!
        </Typography>
        <Typography variant="body2">
          Your request could not be completed. Please contact{' '}
          <Link href="mailto:support@marvelandsnap.com">
            support@marvelandsnap.com
          </Link>{' '}
          for assistance.
        </Typography>
      </Box>
    );
  } else {
    formContent = (
      <form onSubmit={handleSubmit(onSubmit)}>
        <FieldsWrapper mode="grey">
          <Controller
            control={control}
            name="firstName"
            render={({ field: { onChange, ref } }) => (
              <div className={FormStyles.field}>
                <label htmlFor="firstName" className={FormStyles.label}>
                  first name
                </label>
                <TextField
                  id="firstName"
                  variant="outlined"
                  onChange={onChange}
                  fullWidth
                  inputRef={ref}
                  className={FormStyles.input}
                />
              </div>
            )}
            rules={{ required: true }}
          />

          <Controller
            control={control}
            name="lastName"
            render={({ field: { onChange, ref } }) => (
              <div className={FormStyles.field}>
                <label htmlFor="lastName" className={FormStyles.label}>
                  last name
                </label>
                <TextField
                  id="lastName"
                  variant="outlined"
                  onChange={onChange}
                  fullWidth
                  inputRef={ref}
                  className={FormStyles.input}
                />
              </div>
            )}
            rules={{ required: true }}
          />

          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, ref } }) => (
              <div className={FormStyles.field}>
                <label htmlFor="email" className={FormStyles.label}>
                  email
                </label>
                <TextField
                  id="email"
                  variant="outlined"
                  onChange={onChange}
                  fullWidth
                  inputRef={ref}
                  className={FormStyles.input}
                />
              </div>
            )}
            rules={{
              required: true,
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                message: 'invalid email address',
              },
            }}
          />

          <Controller
            control={control}
            name="message"
            render={({ field: { onChange, ref } }) => (
              <div className={FormStyles.field}>
                <label htmlFor="message" className={FormStyles.label}>
                  your message…
                </label>
                <TextField
                  id="message"
                  variant="outlined"
                  onChange={onChange}
                  fullWidth
                  multiline={true}
                  minRows={3}
                  maxRows={10}
                  inputRef={ref}
                  className={FormStyles.input}
                />
              </div>
            )}
            rules={{ required: true }}
          />

          <div className={FormStyles.recaptcha}>
            <ReCAPTCHA
              sitekey={siteKey}
              ref={recaptchaRef}
              onChange={onRecaptchaChange}
            />
          </div>

          {errors.firstName ||
          errors.lastName ||
          errors.message ||
          recaptchaError ? (
            <Typography
              style={{ marginBottom: 20 }}
              className={FormStyles.error}
            >
              All fields are mandatory
            </Typography>
          ) : errors.email ? (
            <Typography
              style={{ marginBottom: 20 }}
              className={FormStyles.error}
            >
              Please enter a valid email address
            </Typography>
          ) : null}
        </FieldsWrapper>

        <div className={FormStyles.wrapper}>
          <Button
            size="large"
            color="primary"
            type="submit"
            variant="contained"
            disabled={submitting}
            disableElevation
          >
            Submit
          </Button>
          {submitting && (
            <CircularProgress size={24} className={FormStyles.buttonProgress} />
          )}
        </div>
      </form>
    );
  }

  return <Box>{formContent}</Box>;
};

export default ContactForm;
